import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigationType, useParams } from 'react-router';
import {
  ADD_FAVORITE_PROFESSIONAL,
  BLOCK_RECENT_PROFESSIONAL,
  changeHubFilter,
  FETCH_FAVORITE_PROFESSIONALS,
  FETCH_RECENT_PROFESSIONALS,
  FETCH_RECOMMENDED_PROFESSIONALS_ACTION,
  getAllPaymnets,
} from '../../actions';
import { clearSuccess } from '../../actions/success';
import { createSuccessSelector } from '../../api/selectors';
import history from '../../browserHistory';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
// NOTE(DS migration): Tabs/Tab/TabList/TabPanel + CardSpinner/CustomSpinner are commonComponents
// with no DS-primitive equivalent yet — kept (separate tabs/spinner migration category).
import { CardSpinner, CustomSpinner, Tab, TabList, TabPanel, Tabs } from '../../commonComponents';
import { ProfessionalHubRedesign } from '../../constants/ff.posthog';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useLoading } from '../../hooks';
import { useAppSelector } from '../../hooks/useAppSelector';
import ChevronRight from '../../images/arrow_right.svg';
import MailSvg from '../../images/mail.svg';
import { PaymentWarningBanner } from '../PaymentWarningBanner';
import ConfirmPopup from '../shared/ConfirmPopup';
import { FavoritesList } from './components/FavoritesList/FavoritesList';
import { useFavoritesList } from './components/FavoritesList/hooks/useFavoritesList';
import { FilterSelect } from './components/FilterSelect/FilterSelect';
import { useRecentProfessionalsList } from './components/RecentProfessionalsList/hooks/useRecentProfessionalsList';
import { RecentProfessionalsList } from './components/RecentProfessionalsList/RecentProfessionalsList';
import { RecommendedProsFeaturePopup } from './components/RecommendedProsFeaturePopup';
import { useRecommendedProsList } from './components/RecommendedProsList/hooks/useRecommendedProsList';
import { RecommendedProsList } from './components/RecommendedProsList/RecommendedProsList';
import { FavoriteAddedPopup } from './components/shared/FavoriteAddedPopup';
import { TAB_INDEX, TABS } from './utils';

const actions = [
  FETCH_FAVORITE_PROFESSIONALS,
  FETCH_RECENT_PROFESSIONALS,
  FETCH_RECOMMENDED_PROFESSIONALS_ACTION,
];

const successAddToFavoritesSelector = createSuccessSelector([ADD_FAVORITE_PROFESSIONAL]);
const successBlockProfessionalSelector = createSuccessSelector([BLOCK_RECENT_PROFESSIONAL]);

export const getInitialTab = (type) => {
  if (type === TABS.RECENT_PROFESSIONALS) return TAB_INDEX.RECENT_PROFESSIONALS;
  if (type === TABS.RECOMMENDED_PROS) return TAB_INDEX.RECOMMENDED_PROS;
  return TAB_INDEX.FAVORITES_LIST;
};

export const ProfessionalsHub = () => {
  const navType = useNavigationType();
  const params = useParams();
  const { type } = params;
  const dispatch = useDispatch();
  const isAddedToFavoritesSuccess = useSelector((state) => successAddToFavoritesSelector(state));
  const isBlockedProfessionalSuccess = useSelector((state) =>
    successBlockProfessionalSelector(state),
  );
  const { professionalHubFilter: filter, isProfessionalHubInitialized } = useAppSelector(
    (state) => state.user,
  );
  const [showSuccessAddedToFavoritesModal, setShowSuccessAddedToFavoritesModal] = useState(false);
  const [showSuccessBlockProfessionalModal, setShowSuccessBlockProfessionalModal] = useState(false);
  const [activeTab, setActiveTab] = useState(getInitialTab(type));
  const firstRenderRef = useRef(true);
  const initialDataLoadRef = useRef(false);
  const wasLoadingRef = useRef(false);
  const favoritedProfessionalNameRef = useRef(null);

  const isBackNavigation = isProfessionalHubInitialized && navType === 'POP';

  const { isLoading } = useLoading(actions);
  const isNewDesign = useFeatureFlag(ProfessionalHubRedesign);

  const { favoriteProfessionals, fetchFavoriteProfessionals } = useFavoritesList();
  const { recentProfessionals, fetchRecentProfessionals } = useRecentProfessionalsList();
  const { recommendedProfessionals, fetchRecommendedPros } = useRecommendedProsList();

  const onChangeTab = useCallback((tab) => {
    setActiveTab(tab);

    let selectedTab = TABS.FAVORITES_LIST;
    switch (tab) {
      case TAB_INDEX.RECENT_PROFESSIONALS:
        selectedTab = TABS.RECENT_PROFESSIONALS;
        break;
      case TAB_INDEX.RECOMMENDED_PROS:
        selectedTab = TABS.RECOMMENDED_PROS;
        break;
      default:
        break;
    }
    history.replace(`/professionals-hub/${selectedTab}`);
  }, []);

  useEffect(() => {
    dispatch(getAllPaymnets());

    // Reset filter when component unmounts (navigating away from professionals hub)
    return () => {
      dispatch(changeHubFilter('all'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!firstRenderRef.current) {
      if (isAddedToFavoritesSuccess) {
        setShowSuccessAddedToFavoritesModal(true);
        dispatch(clearSuccess(ADD_FAVORITE_PROFESSIONAL));
      }

      if (isBlockedProfessionalSuccess) {
        setShowSuccessBlockProfessionalModal(true);
        dispatch(clearSuccess(BLOCK_RECENT_PROFESSIONAL));
      }
    } else {
      dispatch(clearSuccess([ADD_FAVORITE_PROFESSIONAL, BLOCK_RECENT_PROFESSIONAL]));
      firstRenderRef.current = false;
    }
  }, [isAddedToFavoritesSuccess, isBlockedProfessionalSuccess, dispatch]);

  useEffect(() => {
    if (isBackNavigation && !isAddedToFavoritesSuccess && !isBlockedProfessionalSuccess) {
      return;
    }

    (async () => {
      await Promise.all([
        fetchFavoriteProfessionals(),
        fetchRecentProfessionals(),
        fetchRecommendedPros(),
      ]);
    })();
  }, [filter, isAddedToFavoritesSuccess, isBlockedProfessionalSuccess]);

  useEffect(() => {
    if (isLoading) {
      wasLoadingRef.current = true;
      return;
    }

    if (!wasLoadingRef.current || isBackNavigation) return;

    // Only auto-select tab on initial data load, not when filter changes
    if (!initialDataLoadRef.current) {
      initialDataLoadRef.current = true;

      if (favoriteProfessionals?.length) {
        onChangeTab(TAB_INDEX.FAVORITES_LIST);
      } else if (recentProfessionals?.length) {
        onChangeTab(TAB_INDEX.RECENT_PROFESSIONALS);
      } else if (recommendedProfessionals?.length) {
        onChangeTab(TAB_INDEX.RECOMMENDED_PROS);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  if (!isNewDesign && isLoading) {
    return <CardSpinner />;
  }

  return (
    <div className="component_container">
      <div
        className="floating_container"
        style={
          isNewDesign
            ? {
                flexDirection: 'column',
                justifyContent: 'flex-start',
                maxWidth: 1400,
                minWidth: 0,
                boxSizing: 'border-box',
              }
            : {
                flexDirection: 'column',
                justifyContent: 'flex-start',
                marginInline: '20px',
              }
        }
      >
        {isNewDesign && isLoading && <CustomSpinner isLoading />}
        {!(isNewDesign && isLoading) && (
          <>
            {/* LEGACY-HACK (cleanup candidate): bleeds the banner past floating_container padding. */}
            <PaymentWarningBanner containerStyle={{ marginTop: '-10px', marginInline: '-10px' }} />
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              <h1 className="global_font f-dark" style={{ alignSelf: 'center' }}>
                Professionals Hub
              </h1>

              <div
                style={
                  isNewDesign
                    ? {
                        marginTop: 20,
                        display: 'flex',
                        justifyContent: 'space-between',
                        paddingLeft: 80,
                        paddingRight: 80,
                      }
                    : {
                        width: '80%',
                        marginLeft: 80,
                        marginTop: 20,
                        display: 'flex',
                        justifyContent: 'space-between',
                      }
                }
              >
                <FilterSelect />
                <button
                  data-cy="view-invites-button"
                  type="button"
                  style={{
                    all: 'unset',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                  }}
                  onClick={() => history.push('/invites')}
                >
                  <img src={MailSvg} alt="mail" style={{ width: 24, height: 24, marginLeft: 10 }} />
                  {/* fontSize 18 → body_large (16); cursor has no DS prop, kept inline */}
                  <Typography
                    as="span"
                    bold
                    size="body_large"
                    color={colors.secondary['500']}
                    style={{ cursor: 'pointer' }}
                  >
                    View Invites
                  </Typography>
                  <img src={ChevronRight} alt="arrow" style={{ width: 16, height: 16 }} />
                </button>
              </div>

              <div
                style={
                  isNewDesign
                    ? { marginTop: 40, marginBottom: 60, paddingLeft: 60, paddingRight: 60 }
                    : { width: '80%', alignSelf: 'center', marginTop: 40, marginBottom: 120 }
                }
              >
                <Tabs
                  initialTab={activeTab}
                  onChangeTab={onChangeTab}
                  {...(isNewDesign && {
                    style: { alignItems: 'flex-start' },
                    tabListStyle: { marginBottom: 0 },
                    tabPanelStyle: { borderTop: '1px solid #E9F1F4', paddingTop: 20 },
                  })}
                >
                  <TabList>
                    <Tab
                      dataCy="favorites-tab"
                      title="Favorites"
                      totalCount={favoriteProfessionals?.length}
                    />
                    <Tab
                      dataCy="worked-with-tab"
                      title="Worked With"
                      totalCount={recentProfessionals?.length}
                    />
                    <Tab
                      dataCy="new-to-you-tab"
                      title="New to You"
                      totalCount={recommendedProfessionals?.length}
                    />
                  </TabList>
                  <TabPanel>
                    <FavoritesList />
                    <RecentProfessionalsList
                      onFavoriteProfessional={(name) => {
                        favoritedProfessionalNameRef.current = name;
                      }}
                    />
                    <RecommendedProsList />
                  </TabPanel>
                </Tabs>
              </div>
            </div>
          </>
        )}
      </div>

      <RecommendedProsFeaturePopup />

      {showSuccessAddedToFavoritesModal && !isNewDesign && (
        <ConfirmPopup
          dataCy="new-favorite-added-popup"
          closePopupDataCy="new-favorite-modal-backdrop"
          title="New Favorite Added"
          description={
            <>
              This professional has been added onto your
              <br />
              favorites list tab.
            </>
          }
          descriptionStyle={{ marginBottom: 60 }}
          rightButtonText="Close"
          rightButtonAction={() => setShowSuccessAddedToFavoritesModal(false)}
          rightButtonDataCy="new-favorite-close-button"
        />
      )}

      {showSuccessAddedToFavoritesModal && isNewDesign && (
        <FavoriteAddedPopup
          professionalName={favoritedProfessionalNameRef.current ?? ''}
          onClose={() => {
            setShowSuccessAddedToFavoritesModal(false);
            favoritedProfessionalNameRef.current = null;
          }}
        />
      )}

      {showSuccessBlockProfessionalModal && (
        <ConfirmPopup
          dataCy="blocked-professional-popup"
          closePopupDataCy="blocked-professional-modal-backdrop"
          title="Blocked Professional"
          description={
            <>
              This professional has been blocked and will not be able to apply to upcoming shifts.
            </>
          }
          descriptionStyle={{ marginBottom: 60 }}
          rightButtonText="Close"
          rightButtonAction={() => setShowSuccessBlockProfessionalModal(false)}
          rightButtonDataCy="close-button"
        />
      )}
    </div>
  );
};
